import { WEBINAR_STATUSES, WebinarInput } from "./types";
import { PRESENTER_PHOTOS } from "./photos";

// Checks the dashboard's webinar form before saving. The limits match the webinar_events table
// in supabase/migrations/, which enforces them again, so a webinar the form accepts always saves.

export type WebinarErrors = Partial<Record<keyof WebinarInput, string>>;

export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const HTTPS = /^https:\/\/\S+$/;
/** Addresses taken by fixed pages under /events/ */
const RESERVED_SLUGS = ["privacy"];

const tooLong = (value: string | undefined, max: number) => (value ?? "").length > max;

export function validateWebinar(input: WebinarInput): WebinarErrors {
  const errors: WebinarErrors = {};
  const start = Date.parse(input.startAt);
  const end = Date.parse(input.endAt);

  if (!input.title.trim()) errors.title = "Enter a title.";
  else if (tooLong(input.title, 200)) errors.title = "Keep the title under 200 characters.";

  if (!input.slug) errors.slug = "Enter a web address.";
  else if (!SLUG_PATTERN.test(input.slug)) errors.slug = "Use lower-case letters, numbers and single hyphens, e.g. iso-13485-readiness.";
  else if (tooLong(input.slug, 100)) errors.slug = "Keep the web address under 100 characters.";
  else if (RESERVED_SLUGS.includes(input.slug)) errors.slug = `"${input.slug}" is used by another page. Choose a different web address.`;

  if (!input.summary.trim()) errors.summary = "Enter a short summary for the webinar card.";
  else if (tooLong(input.summary, 500)) errors.summary = "Keep the summary under 500 characters.";

  if (!input.description.trim()) errors.description = "Enter a description for the webinar's page.";
  else if (tooLong(input.description, 5000)) errors.description = "Keep the description under 5,000 characters.";

  if (input.topics.some(topic => topic.length > 200)) errors.topics = "Keep each topic under 200 characters.";
  if (tooLong(input.presenter, 120)) errors.presenter = "Keep the name under 120 characters.";
  if (tooLong(input.presenterRole, 120)) errors.presenterRole = "Keep the role under 120 characters.";
  if (input.presenterPhoto && !PRESENTER_PHOTOS[input.presenterPhoto]) errors.presenterPhoto = "Choose a photo from the list.";

  // Start and end share one date, so a missing date is reported once, under the start
  if (Number.isNaN(start)) errors.startAt = "Enter the date and start time.";
  else if (Number.isNaN(end)) errors.endAt = "Enter the end time.";
  else if (end <= start) errors.endAt = "The end time must be after the start time.";

  if (input.registrationDeadline !== undefined) {
    const deadline = Date.parse(input.registrationDeadline);
    if (Number.isNaN(deadline)) errors.registrationDeadline = "Enter both a date and a time, or leave both empty.";
    else if (!Number.isNaN(start) && deadline > start) errors.registrationDeadline = "Registration must close before the webinar starts.";
  }

  if (input.capacity !== undefined && (!Number.isInteger(input.capacity) || input.capacity < 1)) {
    errors.capacity = "Enter a whole number of seats, or leave it empty for no limit.";
  }
  if (tooLong(input.meetingPlatform, 60)) errors.meetingPlatform = "Keep the platform name under 60 characters.";
  if (input.recordingUrl && !HTTPS.test(input.recordingUrl)) errors.recordingUrl = "Paste the full address, starting with https://";
  if (input.resourceUrl && !HTTPS.test(input.resourceUrl)) errors.resourceUrl = "Paste the full address, starting with https://";
  if (!WEBINAR_STATUSES.includes(input.status)) errors.status = "Choose a status.";

  return errors;
}
